import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, MessageSquare } from "lucide-react";
import type {
  NegotiationThread,
  NegotiationThreadStatus,
  NegotiationCampaign,
} from "@/types/negotiations";
import type { VendorLabel } from "@/hooks/useVendorLabels";

/**
 * Formats the thread subtext showing target and floor discount percentages.
 * Returns e.g. "Target 2.5% · Floor 1%", "Target 2.5%", "Floor 1%", or "—".
 */
function formatDiscountSubtext(
  targetPct: number | null | undefined,
  floorPct: number | null | undefined
): string {
  const parts: string[] = [];
  if (targetPct != null) parts.push(`Target ${targetPct}%`);
  if (floorPct != null) parts.push(`Floor ${floorPct}%`);
  return parts.length > 0 ? parts.join(" · ") : "—";
}

const STATUS_STYLES: Record<NegotiationThreadStatus, string> = {
  active: "bg-primary/15 text-primary border-primary/30",
  needs_attention: "bg-warning/15 text-warning border-warning/30",
  agreed: "bg-success/15 text-success border-success/30",
  declined: "bg-destructive/15 text-destructive border-destructive/30",
  abandoned: "bg-muted/50 text-muted-foreground border-border",
};

const STATUS_LABELS: Record<NegotiationThreadStatus, string> = {
  active: "Active",
  needs_attention: "Needs Attention",
  agreed: "Agreed",
  declined: "Declined",
  abandoned: "Abandoned",
};

function timeAgo(iso: string | null): string {
  if (!iso) return "";
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return "";
  }
}

/** Convert a hex/named color to a light background tint for chips. */
function labelStyle(color: string | null): React.CSSProperties {
  if (!color) return {};
  return { backgroundColor: `${color}22`, borderColor: `${color}66`, color };
}

/** Small inline label chip — mirrors the LabelChip in VendorsTable. */
function LabelChip({ label }: { label: VendorLabel }) {
  return (
    <span
      aria-label={`Label: ${label.name}`}
      className="inline-flex items-center text-[10px] font-medium px-1.5 py-0 rounded border leading-4"
      style={
        label.color
          ? labelStyle(label.color)
          : {
              backgroundColor: "hsl(var(--primary) / 0.1)",
              borderColor: "hsl(var(--primary) / 0.3)",
              color: "hsl(var(--primary))",
            }
      }
    >
      {label.name}
    </span>
  );
}

type StatusFilterMode = "all" | "needs_attention";

interface ThreadListProps {
  threads: NegotiationThread[];
  activeId?: string;
  onSelect: (id: string) => void;
  /** Campaigns available for the filter dropdown. */
  campaigns?: NegotiationCampaign[];
  /** Map of vendor_name → VendorLabel[] for showing label chips on each row. */
  labelsByVendor?: Record<string, VendorLabel[]>;
}

export function ThreadList({
  threads,
  activeId,
  onSelect,
  campaigns = [],
  labelsByVendor = {},
}: ThreadListProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilterMode>("all");
  const [selectedLabelId, setSelectedLabelId] = useState<string>("");
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>("");

  const attentionCount = threads.filter((t) => t.status === "needs_attention").length;

  // Build the full set of labels that appear across all threads (for filter dropdown).
  const allLabelsInList: VendorLabel[] = [];
  const seenLabelIds = new Set<string>();
  for (const thread of threads) {
    const vendorName = thread.vendor_name ?? thread.supplier_name ?? "";
    const labels = labelsByVendor[vendorName] ?? [];
    for (const l of labels) {
      if (!seenLabelIds.has(l.id)) {
        seenLabelIds.add(l.id);
        allLabelsInList.push(l);
      }
    }
  }

  // Apply filters in order: status → campaign → label
  let visibleThreads = threads;

  if (statusFilter === "needs_attention") {
    visibleThreads = visibleThreads.filter((t) => t.status === "needs_attention");
  }

  if (selectedCampaignId) {
    visibleThreads = visibleThreads.filter((t) => t.campaign_id === selectedCampaignId);
  }

  if (selectedLabelId) {
    visibleThreads = visibleThreads.filter((t) => {
      const vendorName = t.vendor_name ?? t.supplier_name ?? "";
      const labels = labelsByVendor[vendorName] ?? [];
      return labels.some((l) => l.id === selectedLabelId);
    });
  }

  const filtersActive = selectedCampaignId !== "" || selectedLabelId !== "";

  const selectClass =
    "h-6 rounded border border-border bg-background text-[11px] text-muted-foreground px-1 focus:outline-none focus:ring-1 focus:ring-primary/40 transition-colors hover:border-primary/40";

  if (threads.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-16 px-4 text-center">
        <MessageSquare className="w-10 h-10 text-muted-foreground/40 mb-3" />
        <p className="text-sm text-muted-foreground">No negotiations yet</p>
        <p className="text-xs text-muted-foreground/60 mt-1">Start one using the button above</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* ── Status filter chips ── */}
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-border/50 flex-shrink-0">
        <button
          onClick={() => setStatusFilter("all")}
          className={cn(
            "px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors border",
            statusFilter === "all"
              ? "bg-primary/10 text-primary border-primary/30"
              : "bg-transparent text-muted-foreground border-border hover:bg-muted/40 hover:text-foreground"
          )}
        >
          All
          <span className="ml-1 opacity-60">{threads.length}</span>
        </button>
        <button
          onClick={() => setStatusFilter("needs_attention")}
          className={cn(
            "flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors border",
            statusFilter === "needs_attention"
              ? "bg-warning/15 text-warning border-warning/30"
              : "bg-transparent text-muted-foreground border-border hover:bg-muted/40 hover:text-foreground"
          )}
        >
          <AlertCircle className="w-3 h-3" />
          Needs attention
          {attentionCount > 0 && (
            <span
              className={cn(
                "inline-flex items-center justify-center rounded-full text-[10px] font-bold px-1 min-w-[16px] h-[16px]",
                statusFilter === "needs_attention"
                  ? "bg-warning text-warning-foreground"
                  : "bg-warning/80 text-warning-foreground"
              )}
            >
              {attentionCount}
            </span>
          )}
        </button>
      </div>

      {/* ── Campaign + label filter selects ── */}
      {(campaigns.length > 0 || allLabelsInList.length > 0) && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-border/50 flex-shrink-0 flex-wrap">
          {campaigns.length > 0 && (
            <select
              aria-label="Filter by campaign"
              value={selectedCampaignId}
              onChange={(e) => setSelectedCampaignId(e.target.value)}
              className={selectClass}
            >
              <option value="">Campaign</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}

          {allLabelsInList.length > 0 && (
            <select
              aria-label="Filter by label"
              value={selectedLabelId}
              onChange={(e) => setSelectedLabelId(e.target.value)}
              className={selectClass}
            >
              <option value="">Label</option>
              {allLabelsInList.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          )}

          {filtersActive && (
            <button
              onClick={() => {
                setSelectedCampaignId("");
                setSelectedLabelId("");
              }}
              className="px-1.5 py-0.5 rounded text-[11px] text-muted-foreground hover:text-foreground transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* ── Thread list ── */}
      {visibleThreads.length === 0 ? (
        <div className="flex flex-col items-center justify-center flex-1 py-10 px-4 text-center">
          <AlertCircle className="w-8 h-8 text-muted-foreground/30 mb-2" />
          <p className="text-sm text-muted-foreground">
            {statusFilter === "needs_attention" && !filtersActive
              ? "No threads need attention"
              : "No threads match the current filters"}
          </p>
          <button
            onClick={() => {
              setStatusFilter("all");
              setSelectedCampaignId("");
              setSelectedLabelId("");
            }}
            className="mt-2 text-xs text-primary hover:underline"
          >
            Show all threads
          </button>
        </div>
      ) : (
        <ul className="divide-y divide-border/50 overflow-y-auto flex-1">
          {visibleThreads.map((thread) => {
            const vendorName = thread.vendor_name ?? thread.supplier_name ?? "";
            const threadLabels = labelsByVendor[vendorName] ?? [];
            const campaign = campaigns.find((c) => c.id === thread.campaign_id);

            return (
              <li key={thread.id}>
                <button
                  onClick={() => onSelect(thread.id)}
                  className={cn(
                    "w-full text-left px-4 py-3.5 transition-colors hover:bg-muted/40 focus:outline-none",
                    activeId === thread.id && "bg-primary/10 border-l-2 border-primary"
                  )}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-medium text-foreground truncate">
                      {thread.vendor_name ?? thread.supplier_name ?? thread.supplier_email}
                    </p>
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-[10px] px-1.5 py-0 flex-shrink-0 border",
                        STATUS_STYLES[thread.status]
                      )}
                    >
                      {STATUS_LABELS[thread.status]}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground truncate mb-1">
                    {formatDiscountSubtext(thread.target_discount_pct, thread.floor_discount_pct)}
                  </p>

                  {/* Campaign name + label chips */}
                  {(campaign || threadLabels.length > 0) && (
                    <div className="flex items-center flex-wrap gap-1 mt-1">
                      {campaign && (
                        <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0 rounded border leading-4 bg-muted/60 text-muted-foreground border-border/60">
                          {campaign.name}
                        </span>
                      )}
                      {threadLabels.map((label) => (
                        <LabelChip key={label.id} label={label} />
                      ))}
                    </div>
                  )}

                  {thread.last_message_at && (
                    <p className="text-[10px] text-muted-foreground/50 mt-1">
                      {timeAgo(thread.last_message_at)}
                    </p>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
